/**
 * Which GA4 metrics Explore shows (cards and table columns). AgencyAnalytics
 * keeps the set across views and reloads and puts a newly enabled metric
 * first; it starts on seven defaults. Remembered per organisation in this
 * browser — a viewing preference, so storage failures just fall back to the
 * defaults.
 */

import { useCallback, useState } from 'react';

/** AgencyAnalytics' default order. */
export const DEFAULT_METRICS = [
  'sessions',
  'total_users',
  'user_engagement',
  'views',
  'key_events',
  'event_count',
  'total_purchasers',
];

const storageKey = (organisationId: string) => `synanalytics:ga4-explore-metrics:${organisationId}`;

function load(organisationId: string, known: Set<string>): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey(organisationId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) {
      const kept = parsed.filter((key): key is string => typeof key === 'string' && known.has(key));
      if (kept.length > 0) return kept;
    }
  } catch {
    // Private windows and blocked storage: the defaults are a correct answer.
  }
  return DEFAULT_METRICS.filter((key) => known.has(key));
}

function save(organisationId: string, metrics: string[]): void {
  try {
    window.localStorage.setItem(storageKey(organisationId), JSON.stringify(metrics));
  } catch {
    // Not remembered in this browser; the choice still applies until reload.
  }
}

export function useExploreMetrics(organisationId: string, knownKeys: string[]) {
  const [enabled, setEnabled] = useState(() => load(organisationId, new Set(knownKeys)));
  const update = useCallback(
    (next: string[]) => {
      const kept = next.length > 0 ? next : DEFAULT_METRICS;
      setEnabled(kept);
      save(organisationId, kept);
    },
    [organisationId],
  );
  const toggle = (key: string) =>
    update(enabled.includes(key) ? enabled.filter((item) => item !== key) : [key, ...enabled]);
  const setMany = (keys: string[], on: boolean) =>
    update(
      on
        ? [...keys.filter((key) => !enabled.includes(key)), ...enabled]
        : enabled.filter((key) => !keys.includes(key)),
    );
  return { enabled, toggle, setMany };
}
